import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Box,
  Grid,
  Tabs,
  Tab,
  Alert,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Stack,
} from '@mui/material';
import { Bar, Doughnut, Line, PolarArea } from 'react-chartjs-2';

import { useData } from '../context/DataContext';
import PageHeader from '../components/ui/PageHeader';
import Panel from '../components/ui/Panel';
import FilterBar from '../components/ui/FilterBar';
import StatCard from '../components/ui/StatCard';
import { barOptions, lineOptions, doughnutOptions, polarOptions, categoryDatasetStyle } from '../theme/charts';
import { categoryPalette, brand, layout } from '../theme/tokens';

const round = (n) => Math.round((Number(n) || 0) * 10) / 10;

const formatBucket = (value) => {
  if (typeof value !== 'string') return value;
  const parts = value.split('-').map(Number);
  if (parts.length < 2 || parts.some(Number.isNaN)) return value;
  const isDaily = parts.length >= 3;
  const date = new Date(parts[0], parts[1] - 1, isDaily ? parts[2] : 1);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(
    undefined,
    isDaily ? { day: 'numeric', month: 'short' } : { month: 'short', year: '2-digit' }
  );
};

const TAB_IDS = ['categories', 'assignees', 'trends', 'epics'];

/** Simple sortable-free data table used by the breakdown tabs. */
const BreakdownTable = ({ columns, rows }) => (
  <TableContainer sx={{ maxHeight: 420 }}>
    <Table size="small" stickyHeader>
      <TableHead>
        <TableRow>
          {columns.map((c) => (
            <TableCell key={c.key} align={c.align || 'left'}>
              {c.label}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row, i) => (
          <TableRow key={row.id || i} hover>
            {columns.map((c) => (
              <TableCell
                key={c.key}
                align={c.align || 'left'}
                sx={c.numeric ? { fontVariantNumeric: 'tabular-nums', fontWeight: 600 } : undefined}
              >
                {c.render ? c.render(row) : row[c.key]}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

const Analytics = () => {
  const {
    summaryData,
    categoryData,
    assigneeData,
    timeSeriesData,
    epicData,
    filterOptions,
    setFilterOptions,
    fetchAnalytics,
    clearFilters,
    loading,
    error,
  } = useData();

  const [tab, setTab] = useState(0);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const categories = useMemo(
    () =>
      (categoryData || []).map((c) => ({
        id: c._id || 'Other',
        name: c._id || 'Other',
        count: c.count || 0,
        effort: round(c.totalEffort),
      })),
    [categoryData]
  );

  const totalEffort = useMemo(
    () => categories.reduce((sum, c) => sum + c.effort, 0),
    [categories]
  );

  const assignees = useMemo(
    () =>
      [...(assigneeData || [])]
        .map((a) => ({
          id: a._id || 'Unassigned',
          name: a._id || 'Unassigned',
          count: a.count || 0,
          effort: round(a.totalEffort),
          categories: (a.categories || []).length,
        }))
        // Rank by ticket count, matching the server. Sorting by effort here
        // silently undid the server's ordering: estimatedEffort is 0 on all
        // 13,248 rows and worklogs cover only 25%, so an hours ranking ranks
        // who files worklogs, not who does work.
        .sort((x, y) => y.count - x.count),
    [assigneeData]
  );

  const epics = useMemo(
    () =>
      [...(epicData || [])]
        .map((e) => ({
          id: e._id || 'No epic',
          name: e._id || 'No epic',
          count: e.count || 0,
          effort: round(e.totalEffort),
          people: (e.assignees || []).length,
        }))
        .sort((x, y) => y.effort - x.effort),
    [epicData]
  );

  const categoryNames = categories.map((c) => c.name);

  const effortDoughnut = useMemo(
    () => ({
      labels: categoryNames,
      datasets: [{ label: 'Hours', data: categories.map((c) => c.effort), ...categoryDatasetStyle(categoryNames) }],
    }),
    [categories, categoryNames]
  );

  const countPolar = useMemo(
    () => ({
      labels: categoryNames,
      datasets: [
        {
          label: 'Tickets',
          data: categories.map((c) => c.count),
          backgroundColor: categoryNames.map(
            (n) => (categoryPalette[n] || categoryPalette.Other).soft
          ),
          borderColor: categoryNames.map((n) => (categoryPalette[n] || categoryPalette.Other).main),
          borderWidth: 1.5,
        },
      ],
    }),
    [categories, categoryNames]
  );

  const topAssignees = useMemo(() => assignees.slice(0, 12), [assignees]);

  const assigneeChart = useMemo(
    () => ({
      labels: topAssignees.map((a) => a.name),
      datasets: [
        {
          label: 'Hours',
          data: topAssignees.map((a) => a.effort),
          backgroundColor: `${brand[500]}BF`,
          borderRadius: 6,
        },
      ],
    }),
    [topAssignees]
  );

  const topEpics = useMemo(() => epics.slice(0, 12), [epics]);

  const epicChart = useMemo(
    () => ({
      labels: topEpics.map((e) => e.name),
      datasets: [
        {
          label: 'Hours',
          data: topEpics.map((e) => e.effort),
          backgroundColor: `${categoryPalette.Change.main}BF`,
          borderRadius: 6,
        },
      ],
    }),
    [topEpics]
  );

  const trend = useMemo(() => {
    const rows = timeSeriesData || [];
    if (!rows.length) return { labels: [], datasets: [] };
    const buckets = [...new Set(rows.map((r) => r.date))].sort();
    const names = [...new Set(rows.map((r) => r.category || 'Other'))].sort();
    const index = new Map(rows.map((r) => [`${r.date}|${r.category || 'Other'}`, r.effort || 0]));
    return {
      labels: buckets.map(formatBucket),
      datasets: names.map((name) => {
        const token = categoryPalette[name] || categoryPalette.Other;
        return {
          label: name,
          data: buckets.map((b) => round(index.get(`${b}|${name}`) || 0)),
          borderColor: token.main,
          backgroundColor: token.soft,
          fill: true,
        };
      }),
    };
  }, [timeSeriesData]);

  const ticketTrend = useMemo(() => {
    const rows = timeSeriesData || [];
    if (!rows.length) return { labels: [], datasets: [] };
    const buckets = [...new Set(rows.map((r) => r.date))].sort();
    const totals = buckets.map((b) =>
      rows.filter((r) => r.date === b).reduce((sum, r) => sum + (r.count || 0), 0)
    );
    return {
      labels: buckets.map(formatBucket),
      datasets: [{ label: 'Tickets', data: totals, backgroundColor: `${brand[400]}BF`, borderRadius: 6 }],
    };
  }, [timeSeriesData]);

  const pct = useCallback((v) => (totalEffort ? `${Math.round((v / totalEffort) * 100)}%` : '0%'), [totalEffort]);

  const handleApply = useCallback((next) => fetchAnalytics(next), [fetchAnalytics]);

  return (
    <Box>
      <PageHeader title="Analytics" subtitle="Detailed breakdowns by category, person, epic and time." />

      <Box sx={{ mb: 3 }}>
        <FilterBar
          filterOptions={filterOptions}
          setFilterOptions={setFilterOptions}
          onApply={handleApply}
          onClear={clearFilters}
          loading={loading}
        />
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3} sx={{ mb: 1 }}>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatCard label="Tickets" value={(summaryData?.totalTickets || 0).toLocaleString()} loading={loading} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          {/* "Logged hours", not "Total effort": estimatedEffort is 0 on every
              row and worklogs cover ~25%, so this is hours people recorded,
              not work done. */}
          <StatCard label="Logged hours" value={`${round(summaryData?.totalEffort).toLocaleString()}h`} loading={loading} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatCard label="Contributors" value={summaryData?.uniqueAssignees || 0} loading={loading} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatCard label="Epics" value={summaryData?.uniqueEpics || 0} loading={loading} />
        </Grid>
      </Grid>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 2, mb: 3 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} aria-label="Analytics views">
          <Tab label="Categories" id="tab-categories" aria-controls="panel-categories" />
          <Tab label="People" id="tab-assignees" aria-controls="panel-assignees" />
          <Tab label="Trends" id="tab-trends" aria-controls="panel-trends" />
          <Tab label="Epics" id="tab-epics" aria-controls="panel-epics" />
        </Tabs>
      </Box>

      <Box role="tabpanel" id={`panel-${TAB_IDS[tab]}`} aria-labelledby={`tab-${TAB_IDS[tab]}`}>
        {tab === 0 && (
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, lg: 5 }}>
              <Panel
                title="Effort share"
                loading={loading}
                isEmpty={!categories.length}
                bodyHeight={layout.chartHeight}
              >
                <Doughnut data={effortDoughnut} options={doughnutOptions()} />
              </Panel>
            </Grid>
            <Grid size={{ xs: 12, lg: 7 }}>
              <Panel
                title="Ticket count by category"
                loading={loading}
                isEmpty={!categories.length}
                bodyHeight={layout.chartHeight}
              >
                <PolarArea data={countPolar} options={polarOptions()} />
              </Panel>
            </Grid>
            <Grid size={12}>
              <Panel title="Category detail" loading={loading} isEmpty={!categories.length}>
                <BreakdownTable
                  rows={categories}
                  columns={[
                    {
                      key: 'name',
                      label: 'Category',
                      render: (r) => (
                        <Stack direction="row" alignItems="center" spacing={1.25}>
                          <Box
                            aria-hidden
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              bgcolor: (categoryPalette[r.name] || categoryPalette.Other).main,
                            }}
                          />
                          <Typography variant="body2" fontWeight={600}>
                            {r.name}
                          </Typography>
                        </Stack>
                      ),
                    },
                    { key: 'count', label: 'Tickets', align: 'right', numeric: true },
                    {
                      key: 'effort',
                      label: 'Hours',
                      align: 'right',
                      numeric: true,
                      render: (r) => `${r.effort}h`,
                    },
                    { key: 'share', label: 'Share', align: 'right', render: (r) => pct(r.effort) },
                  ]}
                />
              </Panel>
            </Grid>
          </Grid>
        )}

        {tab === 1 && (
          <Grid container spacing={3}>
            <Grid size={12}>
              <Panel
                title="Effort by person"
                subtitle="Top 12 by hours"
                loading={loading}
                isEmpty={!assignees.length}
                bodyHeight={360}
              >
                <Bar data={assigneeChart} options={barOptions({ horizontal: true })} />
              </Panel>
            </Grid>
            <Grid size={12}>
              <Panel title="People detail" loading={loading} isEmpty={!assignees.length}>
                <BreakdownTable
                  rows={assignees}
                  columns={[
                    { key: 'name', label: 'Assignee' },
                    { key: 'count', label: 'Tickets', align: 'right', numeric: true },
                    { key: 'effort', label: 'Hours', align: 'right', numeric: true, render: (r) => `${r.effort}h` },
                    { key: 'categories', label: 'Categories', align: 'right' },
                    { key: 'share', label: 'Share', align: 'right', render: (r) => pct(r.effort) },
                  ]}
                />
              </Panel>
            </Grid>
          </Grid>
        )}

        {tab === 2 && (
          <Grid container spacing={3}>
            <Grid size={12}>
              <Panel
                title="Effort over time"
                subtitle="Stacked hours by category"
                loading={loading}
                isEmpty={!trend.labels.length}
                bodyHeight={340}
              >
                <Line data={trend} options={lineOptions({ yTitle: 'Hours', stacked: true })} />
              </Panel>
            </Grid>
            <Grid size={12}>
              <Panel
                title="Ticket volume over time"
                loading={loading}
                isEmpty={!ticketTrend.labels.length}
                bodyHeight={280}
              >
                <Bar data={ticketTrend} options={barOptions({ yTitle: 'Tickets' })} />
              </Panel>
            </Grid>
          </Grid>
        )}

        {tab === 3 && (
          <Grid container spacing={3}>
            <Grid size={12}>
              <Panel
                title="Effort by epic"
                subtitle="Top 12 by hours"
                loading={loading}
                isEmpty={!epics.length}
                bodyHeight={360}
              >
                <Bar data={epicChart} options={barOptions({ horizontal: true })} />
              </Panel>
            </Grid>
            <Grid size={12}>
              <Panel title="Epic detail" loading={loading} isEmpty={!epics.length}>
                <BreakdownTable
                  rows={epics}
                  columns={[
                    { key: 'name', label: 'Epic' },
                    { key: 'count', label: 'Tickets', align: 'right', numeric: true },
                    { key: 'effort', label: 'Hours', align: 'right', numeric: true, render: (r) => `${r.effort}h` },
                    { key: 'people', label: 'People', align: 'right' },
                    { key: 'share', label: 'Share', align: 'right', render: (r) => pct(r.effort) },
                  ]}
                />
              </Panel>
            </Grid>
          </Grid>
        )}
      </Box>
    </Box>
  );
};

export default Analytics;
